/**
 * HR selectors — attendance, leave, payroll, recruitment, org chart, employee KPIs.
 */

import type { Attendance, IsoDate, LeaveRequest, MockState, Timesheet } from '../types';

// ---- Attendance / trend -------------------------------------------------

/** Attendance compliance % over the last N days. */
export function selectAttendanceCompliance(state: MockState, days = 30): number {
  const today = new Date('2026-08-31');
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffIso = cutoff.toISOString().slice(0, 10);

  const relevant = state.attendance.filter((a) => a.date >= cutoffIso && a.status !== 'holiday');
  if (relevant.length === 0) return 0;
  const present = relevant.filter((a) => a.status === 'present').length;
  return present / relevant.length;
}

/** Attendance trend (last N days) — percent-present per day. */
export interface AttendanceTrendPoint {
  date: string;
  presentPct: number;
}

export function selectAttendanceTrend(state: MockState, days = 30): AttendanceTrendPoint[] {
  const today = new Date('2026-08-31');
  const series: AttendanceTrendPoint[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const dayRecords = state.attendance.filter((a) => a.date === key);
    const nonHoliday = dayRecords.filter((a) => a.status !== 'holiday');
    const pct = nonHoliday.length === 0
      ? 0
      : nonHoliday.filter((a) => a.status === 'present').length / nonHoliday.length;
    series.push({ date: key, presentPct: pct });
  }
  return series;
}

/** Headcount by org unit. */
export function selectHeadcountByUnit(state: MockState): Array<{ unit: string; count: number }> {
  const acc = new Map<string, number>();
  for (const e of state.employees) {
    if (e.status !== 'active') continue;
    acc.set(e.orgUnitId, (acc.get(e.orgUnitId) ?? 0) + 1);
  }
  return Array.from(acc.entries()).map(([unit, count]) => ({ unit, count }));
}

/** Leave type mix — for HR pie. */
export function selectLeaveTypeMix(state: MockState): Array<{ type: LeaveRequest['type']; count: number }> {
  const acc = new Map<LeaveRequest['type'], number>();
  for (const l of state.leaveRequests) acc.set(l.type, (acc.get(l.type) ?? 0) + 1);
  return Array.from(acc.entries()).map(([type, count]) => ({ type, count }));
}

// ---- Expiring documents -------------------------------------------------

export interface ExpiringDocRow {
  employeeId: string;
  employeeName: string;
  docType: 'iqama' | 'passport' | 'license';
  expiryDate: string;
  daysToExpiry: number;
}

export function selectExpiringDocs(state: MockState, withinDays = 90): ExpiringDocRow[] {
  const today = new Date('2026-08-31');
  const rows: ExpiringDocRow[] = [];
  for (const e of state.employees) {
    const entries: Array<{ type: ExpiringDocRow['docType']; date: string | undefined }> = [
      { type: 'iqama', date: e.iqamaExpiry },
      { type: 'passport', date: e.passportExpiry },
      { type: 'license', date: e.licenseExpiry },
    ];
    for (const { type, date } of entries) {
      if (!date) continue;
      const days = Math.floor(
        (new Date(date).getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (days <= withinDays) {
        rows.push({
          employeeId: e.id,
          employeeName: e.fullName,
          docType: type,
          expiryDate: date,
          daysToExpiry: days,
        });
      }
    }
  }
  rows.sort((a, b) => a.daysToExpiry - b.daysToExpiry);
  return rows;
}

// ---- Employee KPIs (S1-T25) ---------------------------------------------

export interface EmployeeKpis {
  totalAttendance: number;
  attendanceRate: number;
  approvedTimesheetHours: number;
  pendingLeave: number;
  approvedLeave: number;
  latestPayroll?: { period: string; net: number };
}

export function selectEmployeeKpis(state: MockState, employeeId: string): EmployeeKpis {
  const att = state.attendance.filter((a) => a.employeeId === employeeId);
  const nonHoliday = att.filter((a) => a.status !== 'holiday');
  const present = nonHoliday.filter((a) => a.status === 'present').length;
  const ts = state.timesheets.filter((t) => t.employeeId === employeeId && t.approved);
  const leave = state.leaveRequests.filter((l) => l.employeeId === employeeId);
  const payrolls = state.payroll
    .filter((p) => p.employeeId === employeeId)
    .sort((a, b) => (a.period < b.period ? 1 : -1));
  const latest = payrolls[0];

  return {
    totalAttendance: nonHoliday.length,
    attendanceRate: nonHoliday.length === 0 ? 0 : present / nonHoliday.length,
    approvedTimesheetHours: ts.reduce((acc, t) => acc + t.hours, 0),
    pendingLeave: leave.filter((l) => l.status === 'pending').length,
    approvedLeave: leave.filter((l) => l.status === 'approved').length,
    ...(latest ? { latestPayroll: { period: latest.period, net: latest.net } } : {}),
  };
}

// ---- Attendance & timesheets by payroll period (S3-W01) -----------------

/**
 * Period summary for the Attendance → Payroll wizard (S3-W01).
 * Period format: 'YYYY-MM'.
 */
export interface AttendancePeriodSummary {
  period: string;
  attendanceCount: number;
  presentCount: number;
  absentCount: number;
  leaveCount: number;
  totalHours: number;
  employeeCount: number;
}

/** All attendance records falling within a 'YYYY-MM' period. */
export function selectAttendanceByPeriod(state: MockState, period: string): Attendance[] {
  return state.attendance.filter((a) => a.date.startsWith(period));
}

/** All timesheets falling within a 'YYYY-MM' period. */
export function selectTimesheetsByPeriod(state: MockState, period: string): Timesheet[] {
  return state.timesheets.filter((t) => t.date.startsWith(period));
}

/** Roll-up of attendance + timesheet volume for a 'YYYY-MM' payroll period. */
export function selectAttendancePeriodSummary(
  state: MockState,
  period: string,
): AttendancePeriodSummary {
  const records = selectAttendanceByPeriod(state, period);
  const ts = selectTimesheetsByPeriod(state, period);
  const presentCount = records.filter((a) => a.status === 'present').length;
  const absentCount = records.filter((a) => a.status === 'absent').length;
  const leaveCount = records.filter((a) => a.status === 'leave').length;
  const totalHours = ts.filter((t) => t.approved).reduce((acc, t) => acc + t.hours, 0);
  const employeeIds = new Set(records.map((a) => a.employeeId));
  return {
    period,
    attendanceCount: records.length,
    presentCount,
    absentCount,
    leaveCount,
    totalHours,
    employeeCount: employeeIds.size,
  };
}

/** List of distinct 'YYYY-MM' periods present in the attendance fixture. */
export function selectAttendancePeriods(state: MockState): string[] {
  const set = new Set<string>();
  for (const a of state.attendance) set.add(a.date.slice(0, 7));
  return Array.from(set).sort().reverse();
}

// ---- Payroll runs -------------------------------------------------------

export interface PayrollRunSummary {
  period: string;
  employeeCount: number;
  totalBasic: number;
  totalAllowances: number;
  totalDeductions: number;
  totalNet: number;
  status: 'draft' | 'previewed' | 'submitted' | 'posted';
}

export function selectPayrollRuns(state: MockState): PayrollRunSummary[] {
  const byPeriod = new Map<string, PayrollRunSummary>();
  for (const p of state.payroll) {
    const current =
      byPeriod.get(p.period) ??
      ({
        period: p.period,
        employeeCount: 0,
        totalBasic: 0,
        totalAllowances: 0,
        totalDeductions: 0,
        totalNet: 0,
        status: p.status,
      } as PayrollRunSummary);
    current.employeeCount += 1;
    current.totalBasic += p.basic;
    current.totalAllowances += p.allowances;
    current.totalDeductions += p.deductions;
    current.totalNet += p.net;
    current.status = mergeStatus(current.status, p.status);
    byPeriod.set(p.period, current);
  }
  return Array.from(byPeriod.values()).sort((a, b) => (a.period < b.period ? 1 : -1));
}

function mergeStatus(
  a: PayrollRunSummary['status'],
  b: PayrollRunSummary['status'],
): PayrollRunSummary['status'] {
  const order: Record<PayrollRunSummary['status'], number> = {
    draft: 0,
    previewed: 1,
    submitted: 2,
    posted: 3,
  };
  return order[a] <= order[b] ? a : b;
}

// ---- Recruitment (S1-T31) -----------------------------------------------

export interface RecruitmentCandidate {
  id: string;
  name: string;
  role: string;
  stage: 'applied' | 'screening' | 'interview' | 'offer';
  appliedDate: IsoDate;
}

export function selectRecruitmentPipeline(state: MockState): RecruitmentCandidate[] {
  const names = [
    'Mona Al-Rashid',
    'Yousef Al-Dosari',
    'Huda Al-Qahtani',
    'Tariq Al-Mutairi',
    'Rania Al-Harbi',
    'Faisal Al-Shehri',
    'Salma Al-Zahrani',
    'Bandar Al-Otaibi',
    'Lina Al-Ghamdi',
    'Hassan Al-Sudairy',
    'Nada Al-Balawi',
    'Waleed Al-Enezi',
  ];
  const stages: RecruitmentCandidate['stage'][] = ['applied', 'screening', 'interview', 'offer'];
  return names.map((name, i) => {
    const emp = state.employees[i % state.employees.length];
    return {
      id: `cand-${i + 1}`,
      name,
      role: emp?.roleTitle ?? 'Engineer',
      stage: stages[i % stages.length] ?? 'applied',
      appliedDate: `2026-0${(i % 8) + 1}-${String((i % 27) + 1).padStart(2, '0')}`,
    };
  });
}

// ---- Org chart (S1-T32) -------------------------------------------------

export interface OrgUnitHead {
  id: string;
  fullName: string;
  fullNameAr?: string;
  roleTitle: string;
  /** Two-letter initials from fullName, uppercased. */
  initials: string;
}

export interface OrgTreeNode {
  unitId: string;
  nameEn: string;
  nameAr: string;
  headcount: number;
  /** S4-T13 — Senior-most active employee in the unit (earliest joinDate). */
  head?: OrgUnitHead;
  children?: OrgTreeNode[];
}

function computeInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  const first = parts[0]!.charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : '';
  return (first + last).toUpperCase();
}

function findUnitHead(state: MockState, unitId: string): OrgUnitHead | undefined {
  // Senior-most active employee = earliest joinDate. Stable tiebreak on id.
  const candidates = state.employees
    .filter((e) => e.status === 'active' && e.orgUnitId === unitId)
    .slice()
    .sort((a, b) => {
      if (a.joinDate !== b.joinDate) return a.joinDate < b.joinDate ? -1 : 1;
      return a.id.localeCompare(b.id);
    });
  const senior = candidates[0];
  if (!senior) return undefined;
  const head: OrgUnitHead = {
    id: senior.id,
    fullName: senior.fullName,
    roleTitle: senior.roleTitle,
    initials: computeInitials(senior.fullName),
    ...(senior.fullNameAr ? { fullNameAr: senior.fullNameAr } : {}),
  };
  return head;
}

export function selectOrgTree(state: MockState): OrgTreeNode {
  const count = (unitId: string): number =>
    state.employees.filter((e) => e.status === 'active' && e.orgUnitId === unitId).length;

  const unit = (id: string) => state.orgUnits.find((u) => u.id === id);

  const buildNode = (
    unitId: string,
    fallbackEn: string,
    fallbackAr: string,
    children?: OrgTreeNode[],
  ): OrgTreeNode => {
    const u = unit(unitId);
    const head = findUnitHead(state, unitId);
    const node: OrgTreeNode = {
      unitId,
      nameEn: u?.nameEn ?? fallbackEn,
      nameAr: u?.nameAr ?? fallbackAr,
      headcount: count(unitId),
      ...(head ? { head } : {}),
      ...(children ? { children } : {}),
    };
    return node;
  };

  return buildNode('president', 'President', 'الرئيس', [
    buildNode('vpOps', 'VP Operations', 'نائب الرئيس للعمليات', [
      buildNode('engineering', 'Engineering', 'الهندسة'),
      buildNode('operations', 'Operations', 'العمليات'),
    ]),
    buildNode('finance', 'Finance', 'المالية'),
    buildNode('contracts', 'Contracts', 'العقود'),
    buildNode('marketing', 'Marketing', 'التسويق'),
    buildNode('hr', 'HR', 'الموارد البشرية'),
  ]);
}
